import Cookies from '../cookies';

const SIGNUP_REDIRECT_STORAGE_KEY = 'sl-signup-redirect';
// Short-lived cookie that carries the deep link URL across Auth0's cross-origin round-trip.
// sessionStorage is cleared when the browser navigates to auth0.com and back; cookies survive
// because they are domain-scoped, not browsing-context-scoped.
// Consumed exactly once by restoreSignupRedirectFromCookie() on the Auth0 callback page.
const SIGNUP_REDIRECT_AUTH0_COOKIE = 'sl-signup-redirect-auth0-cb';
const COOKIE_UUID = 'sl-user-uuid';
const OLD_COOKIE_UUID = 'sl-user-old-uuid';
const COOKIE_SESSION_ID = 'sl-session-id';
const USER_FLOW = 'sl-user-flow';
const UUID_CHANGE_EVENT_SENT = 'sl-uuid-change-event-sent';
const UK_COUNTRY_COOKIE = 'platgen_uk_country';
const UK_COUNTRY_COOKIE_DAYS = 365;

export function setUuidChangeEventSent(value: boolean) {
  localStorage.setItem(UUID_CHANGE_EVENT_SENT, JSON.stringify(value));
}

export function getUuidChangeEventSent() {
  return JSON.parse(localStorage.getItem(UUID_CHANGE_EVENT_SENT) || 'null');
}

export function setSignupRedirect(url: string): void {
  sessionStorage.setItem(SIGNUP_REDIRECT_STORAGE_KEY, url);
  // Also write a 1-day cookie backup so the URL survives Auth0's cross-origin round-trip.
  // Consumed by restoreSignupRedirectFromCookie() in NHSSignupPage on the callback page.
  Cookies.set(SIGNUP_REDIRECT_AUTH0_COOKIE, url, 1);
}

export function getSignupRedirect(): string | null {
  return sessionStorage.getItem(SIGNUP_REDIRECT_STORAGE_KEY);
}

// Call this once on mount of the Auth0 callback page (NHSSignupPage) to recover the deep
// link URL that was lost when Auth0 navigated the browser cross-origin and cleared sessionStorage.
// Must be called from a useEffect — NOT from a Redux reducer — because it has side effects.
export function restoreSignupRedirectFromCookie(): void {
  if (sessionStorage.getItem(SIGNUP_REDIRECT_STORAGE_KEY)) {
    return; // sessionStorage still intact (e.g. Android), nothing to do
  }
  const fromCookie = Cookies.get(SIGNUP_REDIRECT_AUTH0_COOKIE);
  if (fromCookie) {
    sessionStorage.setItem(SIGNUP_REDIRECT_STORAGE_KEY, fromCookie);
    document.cookie = `${SIGNUP_REDIRECT_AUTH0_COOKIE}=; Max-Age=0; SameSite=Strict; Path=/`;
  }
}

export function removeSignupRedirect(): void {
  sessionStorage.removeItem(SIGNUP_REDIRECT_STORAGE_KEY);
  document.cookie = `${SIGNUP_REDIRECT_AUTH0_COOKIE}=; Max-Age=0; SameSite=Strict; Path=/`;
}

// Deep-link callback schemes the web is allowed to redirect the browser to after onboarding.
// The native app registers a per-flavor bundle-id scheme (com.bighealth.<product>.<env>://) on
// both iOS and Android; the legacy sleepio:// / daylight:// custom schemes are also accepted
// (Android still registers them). Anything else is rejected — the web does
// window.location.href = <redirect>, so we must not allow an open redirect to an arbitrary scheme.
const TRUSTED_DEEP_LINK_SCHEME =
  /^(sleepio|daylight|com\.bighealth\.[a-z0-9.]+):\/\//i;

export function isTrustedDeepLink(
  url: string | null | undefined
): url is string {
  return Boolean(url) && TRUSTED_DEEP_LINK_SCHEME.test(url as string);
}

export function getDeepLinkIfPresent(): string | null {
  const url = getSignupRedirect();
  return isTrustedDeepLink(url) ? url : null;
}

export function setUserFlow(value: string) {
  return Cookies.set(USER_FLOW, value);
}

export function getUserUuid() {
  return Cookies.get(COOKIE_UUID);
}

export function getUserOldUuid() {
  return Cookies.get(OLD_COOKIE_UUID);
}

export function getUserSessionId() {
  return Cookies.get(COOKIE_SESSION_ID);
}

export const UK_VANITY_TO_COUNTRY: Record<string, string> = {
  'nhs-england': 'england',
  'nhs-sleepio': 'scotland',
  wales: 'wales',
  'northern-ireland': 'northern_ireland',
};

export const UK_COUNTRY_TO_VANITY: Record<string, string> = {
  england: 'nhs-england',
  scotland: 'nhs-sleepio',
  wales: 'wales',
  northern_ireland: 'northern-ireland',
};

export function getUserFlowSegments(): string[] {
  const raw = Cookies.get(USER_FLOW);
  if (!raw) {
    return [];
  }
  try {
    return decodeURIComponent(raw).split('/').filter(Boolean);
  } catch (_) {
    return [];
  }
}

export function getUKCountry(): string | null {
  return Cookies.get(UK_COUNTRY_COOKIE) || null;
}

export function setUKCountry(value: string): void {
  Cookies.set(UK_COUNTRY_COOKIE, value, UK_COUNTRY_COOKIE_DAYS);
}

export function clearUKCountry(): void {
  // Cookies.remove() calls set('', -1) which triggers a spurious logError in cookies.js — use document.cookie directly
  document.cookie = `${UK_COUNTRY_COOKIE}=; Max-Age=0; SameSite=Strict; Path=/`;
}
